import { Field } from 'formik'
import { type ReactElement, useState } from 'react'
import type { GroupBase } from 'react-select'
import type { CreatableProps } from 'react-select/creatable'
import Creatable from 'react-select/creatable'
import type {
  ComponentProps,
  UseAsyncPaginateParams,
} from 'react-select-async-paginate'
import { withAsyncPaginate } from 'react-select-async-paginate'

import reactSelectStyles from './styles'
import type {
  PaginatedQueryParams,
  PaginatedResponse,
} from '../../../store/types'
import type { CreateableDropdownOption } from '../types'

type AsyncPaginateCreatableProps<
  CreateableDropdownOption,
  Group extends GroupBase<CreateableDropdownOption>,
  Additional,
  IsMulti extends boolean,
> = CreatableProps<CreateableDropdownOption, IsMulti, Group> &
  UseAsyncPaginateParams<CreateableDropdownOption, Group, Additional> &
  ComponentProps<CreateableDropdownOption, Group, IsMulti>

type AsyncPaginateCreatableType = <
  CreateableDropdownOption,
  Group extends GroupBase<CreateableDropdownOption>,
  Additional,
  IsMulti extends boolean = false,
>(
  props: AsyncPaginateCreatableProps<
    CreateableDropdownOption,
    Group,
    Additional,
    IsMulti
  >,
) => ReactElement

const CreatableAsyncPaginate = withAsyncPaginate(
  Creatable,
) as AsyncPaginateCreatableType

const SelectAsyncPaginate = ({
  fetcher,
  onChange,
  value,
  form,
  field,
  onCreateOption,
  isMulti = false,
  styles,
  createValidation,
  ...rest
}: {
  fetcher: { (args: PaginatedQueryParams): Promise<PaginatedResponse<any>> }
  onChange: { (args: any): Promise<any> }
  form: any
  field: any
  onCreateOption: ((arg: CreateableDropdownOption) => void) | undefined
  value: CreateableDropdownOption | CreateableDropdownOption[]
  isMulti: boolean
  styles: any
  createValidation: (value: string) => boolean
}): JSX.Element => {
  value = Array.isArray(value) ? value : [value]
  const [inputValue, setInputValue] = useState<string>('')

  const onChangeInternal = (
    newValue: CreateableDropdownOption | CreateableDropdownOption[],
  ): void => {
    onChange?.(newValue)
    if (form) {
      form.setFieldValue(field.name, newValue)
    }
  }

  const handleCreateOption = (inputValue: string): void => {
    const checkUrlValidation =
      createValidation === undefined ? true : createValidation(inputValue)

    if (checkUrlValidation && inputValue.trim()) {
      const newItem = {
        label: inputValue,
        value: inputValue,
        __isNew: true,
      }
      onChangeInternal(
        isMulti ? (value ? [...value, newItem] : [newItem]) : newItem,
      )

      onCreateOption?.(newItem)
      setInputValue('')
    }
  }

  const loadOptions: any = async (
    search: string,
    loadedOptions: any,
    { page, hasMore }: { page: number; hasMore: boolean },
  ) => {
    try {
      const { data, meta } = await fetcher({ page, limit: 10, search })
      return {
        options: data,
        hasMore: meta.page < meta.totalPages,
        additional: {
          page: meta.page + 1,
        },
      }
    } catch (error) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 1,
        },
      }
    }
  }

  return (
    <CreatableAsyncPaginate
      className="w-full text-sm font-semibold"
      loadOptions={loadOptions}
      value={value}
      isMulti={isMulti}
      inputValue={inputValue}
      onInputChange={(newValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
          setInputValue(newValue)
        } else if (actionMeta.action === 'menu-close') {
          setInputValue('')
        }
      }}
      onCreateOption={(inputValue) => {
        handleCreateOption(inputValue)
      }}
      onKeyDown={(event: any) => {
        if (event.key === 'Enter' && inputValue.trim()) {
          const target = event.target as HTMLElement
          const isInputElement =
            target.tagName === 'INPUT' ||
            (target.getAttribute('role') === 'combobox' &&
              !target.closest('[role="option"]'))

          if (isInputElement) {
            event.preventDefault()
            event.stopPropagation()
            handleCreateOption(inputValue)
          }
        }
      }}
      onChange={(opt: any) => {
        onChangeInternal(opt)
        setInputValue('')
      }}
      defaultValue={value || null}
      isSearchable={true}
      styles={styles ? styles : reactSelectStyles}
      additional={{
        page: 1,
        hasMore: true,
      }}
      {...rest}
    />
  )
}

// TODO props type
export default function CreatebleAsyncPaginatedDropdown(
  props: any,
): JSX.Element {
  if (props.name) {
    return (
      <Field
        className="w-full text-sm font-semibold"
        {...props}
        component={SelectAsyncPaginate}
      />
    )
  }
  return <SelectAsyncPaginate {...props} />
}
